/**
 * Shape of the form state owned by NewClientModal. Mirrors the shape the
 * webapp expects at POST /api/clients/draft (camelCase) while allowing
 * partials so the operator can Save as Draft at any step.
 */

export interface DraftAddress {
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  zip: string;
  countryCode: string;
}

export interface DraftCompanyDetails {
  companyName: string;
  countryCode: string;
  legalFormId?: number;
  incorporationDate?: string;
  incorporationNumber?: string;
  businessSectorId?: number;
  businessActivityId?: number;
  website?: string;
  telephone?: string;
  contactEmail?: string;
  tin?: string;
  registeredAddress: DraftAddress;
  businessAddress?: DraftAddress;
}

export interface DraftAccountUsage {
  estimatedIncomingVolume?: number;
  estimatedIncomingTxCount?: number;
  estimatedOutgoingVolume?: number;
  estimatedOutgoingTxCount?: number;
  sourceOfIncome?: string;
  monthlyIncome?: number;
  purposeOfAccount?: string;
  intendedUse?: string;
}

export const INDIVIDUAL_ROLES = [
  { id: 1, label: 'Director' },
  { id: 2, label: 'Shareholder' },
  { id: 3, label: 'UBO' },
  { id: 4, label: 'Authorized User' },
  { id: 5, label: 'Legal Representative' },
  { id: 6, label: 'Other' },
] as const;

export interface DraftIndividual {
  firstName: string;
  lastName: string;
  email: string;
  roleId: number;
  gender?: 'M' | 'F' | 'O';
  dateOfBirth?: string;
  nationalityCountryCode?: string;
  residenceCountryCode?: string;
  telephone?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  addressCountryCode?: string;
  ownershipPercent?: number;
  isPep?: boolean;
  pepName?: string;
  pepRelationship?: string;
  usResident?: boolean;
  usTin?: string;
  residenceTin?: string;
  canLogin: boolean;
  /**
   * Initial portal permissions granted at submission. Only meaningful
   * when `canLogin` is true. The operator can revise these later from
   * the Persons tab on the client detail page.
   */
  canViewTransactions?: boolean;
  canCreateTransfer?: boolean;
  /** #62 — approuver, et donc envoyer, un paiement préparé par un autre. */
  canApprovePayment?: boolean;
  canCreateFx?: boolean;
  canManageBeneficiary?: boolean;
}

/**
 * Pricing values returned by the webapp draft GET. The New Client modal
 * does not currently expose any field to edit them — the webapp seeds
 * defaults at draft creation and the operator changes them later via the
 * Pricing tab on the Client Detail page. We carry this on the form so a
 * draft round-trip preserves the value (without it, mapDraftResponse used
 * to throw the data away and the next "Save as draft" looked like the
 * pricing had been wiped).
 */
export interface DraftPricing {
  paymentServiceCode?: string;
  outgoingFeeFixedValue?: string;
  outgoingFeePercentValue?: string;
  incomingFeeFixedValue?: string;
  incomingFeePercentValue?: string;
}

export interface DraftFormState {
  companyDetails: DraftCompanyDetails;
  accountUsage: DraftAccountUsage;
  individuals: DraftIndividual[];
  pricing?: DraftPricing;
}

export function emptyAddress(): DraftAddress {
  return { line1: '', city: '', zip: '', countryCode: '' };
}

export function emptyCompanyDetails(): DraftCompanyDetails {
  return {
    companyName: '',
    countryCode: '',
    registeredAddress: emptyAddress(),
  };
}

export function emptyIndividual(): DraftIndividual {
  return {
    firstName: '',
    lastName: '',
    email: '',
    roleId: 1,
    canLogin: false,
  };
}

export function emptyFormState(): DraftFormState {
  return {
    companyDetails: emptyCompanyDetails(),
    accountUsage: {},
    individuals: [],
  };
}

export interface DraftResponse {
  client_id: string;
  status: string;
}
